/**
 * EnhancedWarehouseManagement.jsx
 *
 * Theme-aware enhanced warehouse management for single-pallet-per-lane system
 * Integrates with existing QR code generation and styling patterns
 */

import React, { useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse, faClipboardList, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import PalletEditor from "./PalletEditor";
import ProductEditor from "./ProductEditor";
import ProductOverview from "./ProductOverview";
import QRViewModal from "./QRViewModal";
import ProductLabelModal from "./ProductLabelModal";
import BulkEditPalletsModal from "./BulkEditPalletsModal";
import HistoricalWarehouseData from "./warehouse/historical_stock";
import { useAlert } from "../../providers/AlertProvider";
import { useAuthProvider } from "../../providers/AuthProvider";
import { bulkMoveStandardPallets, downloadWarehouseLanes, downloadProducts, fetchStandardPalletLaneCapacity, regenerateQRCode } from "../../api/enhancedWarehouse";
import { WarehousePageFunctions } from "../../functions/warehouse_page/warehouse_page_functions";
import logger from "../../utils/logger";

// const fmtCurrency = (v) =>
//     typeof v === "number"
//         ? v.toLocaleString("en-GB", {
//             style: "currency",
//             currency: "GBP",
//             minimumFractionDigits: 2,
//         })
//         : v ?? "—";

export default function EnhancedWarehouseManagement({
    theme = "light",
    management = false,
    customers = [],
    allCustomers = [],
    warehouseLanes = {},
    warehouseProducts = [],
    selectedCustomer = "", // Selected customer ID from parent
    customerName = "", // Customer name from parent
    triggerRefresh,
    isCustomer = false,
    profile = null,
    onCustomerChange, // Simple callback for customer selection changes
    onProductsChange, // Callback for when products data changes
    onWarehouseSummaryChange, // Callback for warehouse summary data (products, pallets)
    allPallets
}) {
    // First get the auth profile and determine which profile to use
    const { showAlert } = useAlert();
    const { profile: authProfile, isAdmin, isWarehouse, isManager, isDev, permissions } = useAuthProvider();

    // Management = manager or dev role
    const isManagement = isManager || isDev;
    
    // Use the profile prop if provided, otherwise fall back to auth profile
    const userProfile = profile || authProfile;
    
    const [selectedCustomerId, setSelectedCustomerId] = useState("");
    const [editingPallet, setEditingPallet] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [palletEditorOpen, setPalletEditorOpen] = useState(false);
    const [productEditorOpen, setProductEditorOpen] = useState(false);
    const [qrModalData, setQrModalData] = useState(null);
    const [labelModalData, setLabelModalData] = useState(null);  // New state for label modal
    const [isGeneratingQR, setIsGeneratingQR] = useState({});
    const [activeTab, setActiveTab] = useState("warehouse"); // "warehouse" or "products"
    const [searchTerm, setSearchTerm] = useState(""); // Search functionality
    const [expandedLanes, setExpandedLanes] = useState({}); // Track expanded/collapsed lanes
    const [isShowingProductDetails, setIsShowingProductDetails] = useState(false); // Track if showing specific product details
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedBulkPalletIds, setSelectedBulkPalletIds] = useState([]);
    const [bulkMoveModalOpen, setBulkMoveModalOpen] = useState(false);
    const [isBulkMoving, setIsBulkMoving] = useState(false);
    const [destinationLaneOptions, setDestinationLaneOptions] = useState([]);

    // Bulk edit state
    const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);

    // Local state for data that needs to refresh
    const [localWarehouseData, setLocalWarehouseData] = useState({});
    const [localProducts, setLocalProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDataReady, setIsDataReady] = useState(false);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [pendingCustomerId, setPendingCustomerId] = useState(selectedCustomerId || "");
    const [allCustomerPallets, setAllCustomerPallets] = useState([]); // Store all customer pallets for product movement
    const [isShowingAllPallets, setIsShowingAllPallets] = useState(false); // Track if showing all pallets view
    const [allPalletsData, setAllPalletsData] = useState([]); // Store all pallets data
    const [loadingAllPallets, setLoadingAllPallets] = useState(false); // Loading state for all pallets
    const [loadingPalletDetails, setLoadingPalletDetails] = useState(false); // Loading state for individual pallet details
    const [customerLocked, setCustomerLocked] = useState(false); // Track if customer selection is locked
    const containerRef = useRef(null);

    const [ showHistoricalData, setShowHistoricalData ] = useState(false);
    
    // Add refs to track and cancel ongoing API requests to prevent race conditions
    const abortControllerRef = useRef(null);
    const currentCustomerIdRef = useRef(selectedCustomerId);
    const debounceTimeoutRef = useRef(null);
    const lastProcessedCustomerRef = useRef(selectedCustomerId); // Track last successfully processed customer



    // Extract all pallets from warehouse data for validation
    const getAllPallets = () => {
        
        // If we have all customer pallets stored (from product detail view), use those for product movement
        if (allCustomerPallets && allCustomerPallets.length > 0) {
            console.log('  ✅ Using allCustomerPallets');
            return allCustomerPallets;
        }
        
        // If we're in the "show all pallets" view, use allPalletsData
        if (isShowingAllPallets && allPalletsData && allPalletsData.length > 0) {
            console.log('  ✅ Using allPalletsData');
            return allPalletsData;
        }
        
        // Otherwise, fall back to pallets from current warehouse data
        const allPallets = [];
        Object.values(localWarehouseData).forEach(laneData => {
            if (laneData.pallets) {
                allPallets.push(...laneData.pallets);
            }
        });
        console.log('  ⚠️ Using localWarehouseData fallback, found:', allPallets.length, 'pallets');
        return allPallets;
    };

    // Modal functions
    const openBulkEditModal = () => {
        setBulkEditModalOpen(true);
    };

    const closeBulkEditModal = () => {
        setBulkEditModalOpen(false);
    };

    const isDark = theme === "dark";
    const color = {
        bg: isDark ? "#080808" : "#fff",
        panel: isDark ? "#333" : "#fff",
        fg: isDark ? "#fff" : "#000",
        border: isDark ? "#fff" : "#000",
        headerBg: isDark ? "#2b2b2b" : "#eee",
        highlight: "rgba(26, 221, 247, 0.6)",
        success: "#28a745",
        warning: "#ffc107",
    };

    const warehouseFunctions = new WarehousePageFunctions(showAlert);

    // Handle product selection to load detailed pallet information
    const handleProductSelect = async (product) => {
        if (!product || !selectedCustomerId) {
            logger.warn('⚠️ Cannot load product pallet details: missing product or customer ID');
            return;
        }
        
        // Set flag to indicate we're showing product-specific details
        setIsShowingProductDetails(true);
        setSelectedProduct(product);
        setSelectedBulkPalletIds([]);
        
        // Switch to warehouse tab to show the pallet details
        setActiveTab("warehouse");
        
        await warehouseFunctions.loadProductPalletDetails(
            product.id,
            parseInt(selectedCustomerId),
            setIsLoading,
            setLocalWarehouseData,
            product, // Pass the full product object
            setAllCustomerPallets // Store all customer pallets for product movement
        );
        

    };

    const selectableProductPallets = useMemo(() => (
        Object.values(localWarehouseData)
            .flatMap(lane => lane.pallets || [])
            .filter(pallet => pallet.pallet_type === 'standard' && pallet.status === 'active')
    ), [localWarehouseData]);

    const openBulkMoveModal = async () => {
        const result = await fetchStandardPalletLaneCapacity();
        if (!result.success) {
            showAlert({
                message: result.message || 'Unable to load empty pallet locations',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        setDestinationLaneOptions(result.data
            .map(lane => ({ laneId: lane.lane_id, emptyCount: Number(lane.empty_locations) }))
            .sort((left, right) => left.laneId.localeCompare(right.laneId)));
        setBulkMoveModalOpen(true);
    };

    const toggleBulkPallet = (palletId) => {
        setSelectedBulkPalletIds(current => current.includes(palletId)
            ? current.filter(id => id !== palletId)
            : [...current, palletId]);
    };

    const handleBulkMove = async (destinationLane) => {
        setIsBulkMoving(true);
        const result = await bulkMoveStandardPallets(selectedBulkPalletIds, destinationLane);
        if (result.success) {
            setBulkMoveModalOpen(false);
            setSelectedBulkPalletIds([]);
            if (selectedProduct) {
                await warehouseFunctions.loadProductPalletDetails(
                    selectedProduct.id,
                    parseInt(selectedCustomerId),
                    setIsLoading,
                    setLocalWarehouseData,
                    selectedProduct,
                    setAllCustomerPallets
                );
            }
            showAlert({
                message: `${result.data.moved_pallet_count} pallets moved to ${destinationLane}`,
                type: 'success',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        }
        setIsBulkMoving(false);
        return result;
    };

    // Handle pallet selection from all pallets view
    const handlePalletSelect = async (pallet) => {
        if (!pallet) {
            logger.warn('⚠️ Cannot select pallet: missing pallet data');
            return;
        }

        if (!selectedCustomerId) {
            logger.warn('⚠️ Cannot select pallet: no customer selected');
            showAlert({
                message: 'Please select a customer first',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }


        setLoadingPalletDetails(true);
        
        try {
            // Import necessary functions
            const { getSessionUUID } = await import('../../components/lib/AuthToken');
            const sessionToken = await getSessionUUID();
            
            // Check if pallet is occupied or empty
            const isOccupied = pallet.occupied || (pallet.items && pallet.items.length > 0) || pallet.total_quantity > 0;
            
            if (isOccupied) {
                // For occupied pallets, fetch complete pallet details with items and products
                
                // Fetch pallet contents using the enhanced warehouse endpoint
                const palletContentsResponse = await fetch(`/enhanced_warehouse/pallet/${pallet.id}/contents`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });
                
                if (!palletContentsResponse.ok) {
                    throw new Error(`Failed to fetch pallet contents: ${palletContentsResponse.statusText}`);
                }
                
                const palletContentsData = await palletContentsResponse.json();
                
                // DEBUG: Log the pallet contents response to check for pending_order field
                logger.info('🔍 PALLET CONTENTS API RESPONSE:', palletContentsData);
                
                let enrichedItems = [];
                if (palletContentsData.success && palletContentsData.pallet && palletContentsData.pallet.items) {
                    // The enhanced warehouse endpoint already includes product details
                    enrichedItems = palletContentsData.pallet.items;
                    
                    // DEBUG: Log the items to check for pending_order field
                    logger.info('🔍 PALLET ITEMS WITH pending_order FIELD:');
                    enrichedItems.forEach((item, index) => {
                        logger.info(`  Item ${index + 1}:`, {
                            id: item.id,
                            quantity: item.quantity,
                            pending_order: item.pending_order,
                            has_pending_order_field: 'pending_order' in item,
                            product_code: item.product?.product_code || 'N/A'
                        });
                    });
                }
                
                // DEBUG: Log what we're about to set in editing pallet
                logger.info('🎯 SETTING EDITING PALLET WITH ITEMS:', {
                    pallet_id: pallet.id,
                    pallet_code: pallet.pallet_code,
                    items_count: enrichedItems.length,
                    items_with_pending_order: enrichedItems.filter(item => item.pending_order).length,
                    sample_item: enrichedItems[0] ? {
                        id: enrichedItems[0].id,
                        pending_order: enrichedItems[0].pending_order,
                        product_code: enrichedItems[0].product?.product_code
                    } : 'No items'
                });

                // Open pallet editor with complete data
                setEditingPallet({
                    ...pallet,
                    status: pallet.status || pallet.pallet_status || 'active', // Map pallet_status to status
                    lane: { id: pallet.lane_id, name: pallet.lane_name },
                    items: enrichedItems,
                    product_items: enrichedItems // Legacy compatibility
                });
                
            } else {
                // For empty pallets, open the pallet editor in add mode
                setEditingPallet({
                    ...pallet,
                    status: pallet.status || pallet.pallet_status || 'active', // Map pallet_status to status
                    lane: { id: pallet.lane_id, name: pallet.lane_name },
                    items: [], // Empty array for adding new products
                    product_items: [] // Legacy compatibility
                });
            }
            
            setPalletEditorOpen(true);
            
        } catch (error) {
            logger.error('❌ Error fetching pallet details:', error);
            showAlert({
                message: `Failed to load pallet details: ${error.message}`,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setLoadingPalletDetails(false);
        }
    };

    // Allow editing for warehouse staff and devs always; all others need perm_warehouse_edit
    const canEdit = !isCustomer && (isDev || isWarehouse || permissions.perm_warehouse_edit);
    const canBulkMove = !isCustomer && (
        isDev || permissions.is_super_admin || permissions.perm_warehouse_edit
    );

    // Function to refresh warehouse data by calling parent refresh
    const refreshWarehouseData = async () => {

        setIsLoading(true);
        setIsDataReady(false);
        
        try {
            if (triggerRefresh) {
                await triggerRefresh();
            }
        } catch (error) {
            logger.error('Error refreshing warehouse data:', error);
        }
        
        // Note: isLoading will be set to false in the useEffect when new data arrives
    };

    // Download warehouse lanes as Excel
    const handleDownloadWarehouseLanes = async () => {
        try {
            const customerId = selectedCustomerId || null;
            const result = await downloadWarehouseLanes(customerId);
            
            if (result.success) {
                showAlert({
                    message: result.message,
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            } else {
                showAlert({
                    message: result.message,
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            showAlert({
                message: 'Failed to download warehouse lanes',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        }
    };

    // Download products as Excel
    const handleDownloadProducts = async () => {
        try {
            const customerId = selectedCustomerId || null;
            const result = await downloadProducts(customerId);
            
            if (result.success) {
                showAlert({
                    message: result.message,
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            } else {
                showAlert({
                    message: result.message,
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            showAlert({
                message: 'Failed to download products',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        }
    };

    // Handle customer user initialization - set their customer ID without locking
    useEffect(() => {
        if (isCustomer && userProfile?.customer_id && !selectedCustomerId) {
            const customerIdString = userProfile.customer_id.toString();
            setSelectedCustomerId(customerIdString);
            setPendingCustomerId(customerIdString);
            currentCustomerIdRef.current = customerIdString;
            lastProcessedCustomerRef.current = customerIdString;
            // Don't lock for customer users - they should naturally have their customer set
        }
    }, [isCustomer, userProfile?.customer_id]); // Remove selectedCustomerId dependency to prevent re-runs

    // Sync with selectedCustomer prop on mount and when it changes (one-time sync) - only for admin users
    useEffect(() => {
        if (!isCustomer && selectedCustomer && selectedCustomer !== selectedCustomerId) {
            setSelectedCustomerId(selectedCustomer);
            setPendingCustomerId(selectedCustomer);
            currentCustomerIdRef.current = selectedCustomer;
            lastProcessedCustomerRef.current = selectedCustomer;
            
            // Lock customer selection if syncing with an already selected customer (admin only)
            setCustomerLocked(true);
        }
    }, [isCustomer, selectedCustomer, selectedCustomerId]); // Include isCustomer to prevent firing for customer users

    // Manual function to load customer warehouse data when Load Data button is clicked
    const loadCustomerData = async () => {
        // Cancel any ongoing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Only trigger this for admin users when they select a customer
        // Customer users get their data loaded directly in loadPageData
        if (!isCustomer && pendingCustomerId) {
            
            // Create new abort controller for this request
            abortControllerRef.current = new AbortController();
            currentCustomerIdRef.current = pendingCustomerId;
            
            // Set the selected customer ID and reset product details flag when customer changes
            setSelectedCustomerId(pendingCustomerId);
            setIsShowingProductDetails(false);
            
            try {
                await warehouseFunctions.loadSelectedCustomerData(
                    pendingCustomerId,
                    setIsLoading,
                    setLocalWarehouseData,
                    setLocalProducts, // Products should be set locally from warehouse summary
                    setAllCustomerPallets, // Clear all customer pallets when loading new customer
                    abortControllerRef.current.signal // Pass abort signal to prevent race conditions
                );
                
                // Clear the abort controller if request completed successfully
                if (currentCustomerIdRef.current === pendingCustomerId) {
                    abortControllerRef.current = null;
                    lastProcessedCustomerRef.current = pendingCustomerId; // Track successful completion
                    // Lock customer selection after successful data load (admin users only)
                    if (pendingCustomerId && !isCustomer) {
                        setCustomerLocked(true);
                    }
                    
                    // Load warehouse summary data and pass to parent component
                    if (onWarehouseSummaryChange) {
                        try {
                            const { getCustomerWarehouseSummary } = await import('../../api/customerWarehouseSummary');
                            const warehouseSummaryResult = await getCustomerWarehouseSummary(parseInt(pendingCustomerId));
                            
                            if (warehouseSummaryResult.success && warehouseSummaryResult.data) {
                                const summaryProducts = warehouseSummaryResult.data.products || [];
                                
                                // Transform pallets for consistency
                                const pallets = [];
                                let palletSource = warehouseSummaryResult.data.pallet_locations || warehouseSummaryResult.data.pallet_codes || [];
                                
                                // Convert to array if needed
                                if (!Array.isArray(palletSource)) {
                                    palletSource = Object.values(palletSource);
                                }
                                
                                palletSource.forEach(pallet => {
                                    pallets.push({
                                        id: pallet.pallet_id || pallet.pallet_code,
                                        pallet_code: pallet.pallet_code,
                                        pallet_type: pallet.pallet_type || null,
                                        lane_id: pallet.lane_id,
                                        lane_name: pallet.lane_name || pallet.lane_id,
                                        customer_id: parseInt(pendingCustomerId),
                                        occupied: pallet.occupied || false,
                                        total_quantity: pallet.total_quantity || 0,
                                        items: pallet.items || []
                                    });
                                });
                                
                                onWarehouseSummaryChange(summaryProducts, pallets);
                                
                                // Also populate allPalletsData so BulkEditPalletsModal has access to pallets
                                setAllPalletsData(pallets);

                            }
                        } catch (error) {
                            logger.warn('⚠️ Failed to load warehouse summary for callback:', error);
                        }
                    }
                }
            } catch (error) {
                if (error.name === 'AbortError') {

                } else {
                    console.error('❌ Error loading customer data:', error);
                }
            }
        } else if (!isCustomer && !pendingCustomerId) {
            // Clear data when no customer is selected

            
            // Cancel any ongoing request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            
            currentCustomerIdRef.current = "";
            setSelectedCustomerId("");
            setLocalWarehouseData({});
            setLocalProducts([]);
            setAllCustomerPallets([]); // Clear stored customer pallets
            setIsShowingProductDetails(false);
            
            // Unlock customer selection when clearing data (admin users only)
            if (!isCustomer) {
                setCustomerLocked(false);

            }
        } else {

        }
    };

    // Function to load all pallets for the selected customer
    const loadAllPallets = async () => {
        if (!selectedCustomerId) {
            showAlert({
                message: 'Please select a customer first',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        setLoadingAllPallets(true);
        setIsShowingAllPallets(true);
        setIsShowingProductDetails(false); // Hide product details when showing all pallets

        try {

            
            // Use the comprehensive warehouse summary to get all pallets with occupancy data
            const { getCustomerWarehouseSummary } = await import('../../api/customerWarehouseSummary');
            const warehouseSummaryResult = await getCustomerWarehouseSummary(parseInt(selectedCustomerId));
            

            
            if (warehouseSummaryResult.success && warehouseSummaryResult.data) {
                const allPallets = [];
                
                // Check if we have pallet_locations (comprehensive data) or pallet_codes (basic data)
                let palletSource = null;
                if (warehouseSummaryResult.data.pallet_locations) {
                    palletSource = warehouseSummaryResult.data.pallet_locations;
                } else if (warehouseSummaryResult.data.pallet_codes) {
                    palletSource = warehouseSummaryResult.data.pallet_codes;
                } else {
                    logger.warn('⚠️ No pallet data found in warehouse summary');
                    setAllPalletsData([]);
                    return;
                }
                
                // Convert to array if it's an object
                let palletsArray = [];
                if (Array.isArray(palletSource)) {
                    palletsArray = palletSource;
                } else {
                    palletsArray = Object.values(palletSource);
                }
                
                // Process each pallet
                palletsArray.forEach((pallet, index) => {
                    
                    const processedPallet = {
                        id: pallet.pallet_id || pallet.pallet_code,
                        pallet_code: pallet.pallet_code,
                        pallet_type: pallet.pallet_type || 'standard',
                        lane_id: pallet.lane_id,
                        lane_name: pallet.lane_name || pallet.lane_id,
                        customer_id: parseInt(selectedCustomerId),
                        qr_link: pallet.pallet_qr_link,
                        pallet_status: pallet.status || 'active',
                        created_at: pallet.created_at,
                        // Include occupancy data if available
                        occupied: pallet.occupied || false,
                        total_quantity: pallet.total_quantity || 0,
                        items: pallet.items || []
                    };
                    
                    allPallets.push(processedPallet);
                });

                setAllPalletsData(allPallets);                // Pass warehouse summary data to parent component if callback is provided
                if (onWarehouseSummaryChange && warehouseSummaryResult.data) {
                    const summaryProducts = warehouseSummaryResult.data.products || [];
                    onWarehouseSummaryChange(summaryProducts, allPallets);

                }
            } else {
                logger.warn('⚠️ Failed to load warehouse summary:', warehouseSummaryResult.error);
                setAllPalletsData([]);
                showAlert({
                    message: 'Failed to load pallets data',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('❌ Error loading all pallets:', error);
            showAlert({
                message: 'Failed to load pallets',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setLoadingAllPallets(false);
        }
    };

    // Sync selectedCustomerId with selectedCustomer prop to maintain state across tab switches
    // TEMPORARILY DISABLED TO PREVENT INFINITE LOOPS
    // useEffect(() => {
    //     // Only sync if:
    //     // 1. selectedCustomer prop exists
    //     // 2. It's different from current selected customer
    //     // 3. It's different from what we're currently processing
    //     // 4. We're not currently loading data (to prevent interference)
    //     if (selectedCustomer && 
    //         selectedCustomer !== selectedCustomerId && 
    //         selectedCustomer !== currentCustomerIdRef.current &&
    //         !isLoading) {
            
    //         logger.log('🔄 Syncing selectedCustomerId with prop (external change):', selectedCustomer);
            
    //         // Cancel any ongoing request before syncing
    //         if (abortControllerRef.current) {
    //             abortControllerRef.current.abort();
    //             console.log('🚫 Cancelled request due to external prop sync');
    //         }
            
    //         // Update refs to track the new customer
    //         currentCustomerIdRef.current = selectedCustomer;
            
    //         setSelectedCustomerId(selectedCustomer);
    //         setPendingCustomerId(selectedCustomer);
    //     }
    // }, [selectedCustomer]); // Remove selectedCustomerId from dependencies to prevent loops

    // Notify parent component when customer selection changes (admin users only)
    useEffect(() => {
        if (!isCustomer && onCustomerChange && selectedCustomerId !== selectedCustomer) {
            // Only notify parent if our internal state differs from the prop (user made a change)
            onCustomerChange(selectedCustomerId);
        }
    }, [isCustomer, selectedCustomerId, onCustomerChange, selectedCustomer]);



    // Notify parent when products data changes
    useEffect(() => {
        if (onProductsChange && localProducts.length > 0) {
            onProductsChange(localProducts);
        }
    }, [localProducts, onProductsChange]);

    // Scroll to top button visibility handler
    useEffect(() => {
        const handleScroll = (event) => {
            let scrollTop = 0;
            
            // Try to get scroll position from the event target first
            if (event && event.target) {
                scrollTop = event.target.scrollTop;
            }
            
            // Fallback to various scroll containers
            if (scrollTop === 0) {
                scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            }
            
            // Also check parent containers that might be scrollable
            if (scrollTop === 0 && containerRef.current) {
                let parent = containerRef.current.parentElement;
                while (parent && scrollTop === 0) {
                    const computedStyle = window.getComputedStyle(parent);
                    if (computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll' || computedStyle.overflow === 'auto' || computedStyle.overflow === 'scroll') {
                        scrollTop = parent.scrollTop;
                        break;
                    }
                    parent = parent.parentElement;
                }
            }
            
            // Scroll detected - logging disabled to reduce console noise
            setShowScrollToTop(scrollTop > 300); // Show button after scrolling 300px
        };

        // Listen for scroll on multiple elements
        window.addEventListener('scroll', handleScroll, true); // Use capture phase
        document.addEventListener('scroll', handleScroll, true);
        
        // Also check initial scroll position
        handleScroll();
        
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            document.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    // Cleanup effect to prevent memory leaks and race conditions
    useEffect(() => {
        return () => {
            // Clear any pending debounce timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            
            // Cancel any ongoing request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();

            }
        };
    }, []);

    // Cleanup effect to cancel ongoing requests and timeouts
    useEffect(() => {
        return () => {
            // Cancel any ongoing API request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();

            }
            
            // Clear any pending debounce timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    // Function to scroll to top
    const scrollToTop = () => {
        // Try to scroll the scrollable parent container first
        if (containerRef.current) {
            let parent = containerRef.current.parentElement;
            while (parent) {
                const computedStyle = window.getComputedStyle(parent);
                if (computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll' || computedStyle.overflow === 'auto' || computedStyle.overflow === 'scroll') {
                    parent.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                    return; // Exit after finding and scrolling the scrollable parent
                }
                parent = parent.parentElement;
            }
        }
        
        // Fallback to window scroll
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
        
        // Additional fallbacks for older browsers
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    };

    // Function to update pallet items in local state without full refresh
    const updatePalletInLocalState = (updatedPallet) => {

        // Update the old warehouse lanes structure
        setLocalWarehouseData(prevData => {
            const newData = { ...prevData };
            
            // Find and update the pallet in the warehouse lanes
            Object.keys(newData).forEach(laneId => {
                if (newData[laneId].pallets) {
                    const palletIndex = newData[laneId].pallets.findIndex(p => p.id === updatedPallet.id);
                    if (palletIndex !== -1) {
                        newData[laneId] = {
                            ...newData[laneId],
                            pallets: newData[laneId].pallets.map((pallet, index) => 
                                index === palletIndex ? { ...pallet, ...updatedPallet } : pallet
                            )
                        };
                    }
                }
            });
            
            return newData;
        });
        
        // Also update the allPalletsData if we're in the show all pallets view
        if (isShowingAllPallets) {

            setAllPalletsData(prevPallets => {
                return prevPallets.map(pallet => {
                    if (pallet.id === updatedPallet.id) {
                        const updated = { ...pallet, ...updatedPallet };

                        return updated;
                    }
                    return pallet;
                });
            });
            
            // Also notify parent component of the updated pallet data
            if (onWarehouseSummaryChange) {
                // Get updated pallets array
                setAllPalletsData(prevPallets => {
                    const updatedPallets = prevPallets.map(pallet => 
                        pallet.id === updatedPallet.id ? { ...pallet, ...updatedPallet } : pallet
                    );
                    
                    // Pass updated data to parent
                    // We don't have access to products here, so pass empty array or get from state
                    onWarehouseSummaryChange([], updatedPallets);

                    
                    return updatedPallets;
                });
            }
        }
    };

    // Expand/Collapse all lanes functions
    const expandAllLanes = () => {
        const allLanes = {};
        Object.keys(localWarehouseData).forEach(laneId => {
            allLanes[laneId] = true;
        });
        setExpandedLanes(allLanes);
    };

    const collapseAllLanes = () => {
        const allLanes = {};
        Object.keys(localWarehouseData).forEach(laneId => {
            allLanes[laneId] = false;
        });
        setExpandedLanes(allLanes);
    };

    const toggleLaneExpansion = (laneId, isExpanded) => {
        setExpandedLanes(prev => ({
            ...prev,
            [laneId]: isExpanded
        }));
    };

    // Update local state when props change with validation
    useEffect(() => {
        const updateData = () => {
            setIsLoading(true);
            setIsDataReady(false);

            // Directly set the data without validation
            setLocalWarehouseData(warehouseLanes);
            setLocalProducts(warehouseProducts);
            setIsDataReady(true);
            
            setIsLoading(false);
        };

        // Add a small delay to ensure all props are fully loaded
        const timeoutId = setTimeout(updateData, 100);
        return () => clearTimeout(timeoutId);
    }, [warehouseLanes, warehouseProducts]);


    // Helper function to parse lane ID for proper sorting
    const parseLaneId = (laneId) => {
        // Parse format: W[warehouse][lane][location][position]
        // Example: W1A001A, W50A002B, W2B001C
        const match = laneId.match(/^W(\d+)([A-Z])(\d+)([A-Z])$/);
        if (match) {
            return {
                warehouse: parseInt(match[1], 10),   // 1, 50, 2
                lane: match[2],                      // A, B, C
                location: parseInt(match[3], 10),    // 1, 2, 3
                position: match[4]                   // A, B, C, D
            };
        }
        return {
            warehouse: 0,
            lane: laneId,
            location: 0,
            position: ''
        };
    };

    const filteredLanes = useMemo(() => {
        let filtered = localWarehouseData;
        
        // Filter by search term
        if (searchTerm.trim()) {
            const searchFiltered = {};
            const searchLower = searchTerm.toLowerCase().trim();
            
            Object.entries(filtered).forEach(([laneId, laneData]) => {
                if (laneData.pallets) {
                    const matchingPallets = laneData.pallets.filter(pallet => {
                        // Check if pallet code matches search term
                        const palletCodeMatch = pallet.pallet_code?.toLowerCase().includes(searchLower);
                        
                        // Check if lane ID matches search term
                        const laneIdMatch = laneId?.toLowerCase().includes(searchLower);
                        
                        // Check if any items in the pallet match the search term
                        const itemsMatch = pallet.items && pallet.items.some(item => {
                            const product = item.product;
                            if (!product) return false;
                            
                            return (
                                product.product_code?.toLowerCase().includes(searchLower) ||
                                product.product_name?.toLowerCase().includes(searchLower) ||
                                product.batch_no?.toLowerCase().includes(searchLower)
                            );
                        });
                        
                        // Return true if any of the criteria match
                        return palletCodeMatch || laneIdMatch || itemsMatch;
                    });
                    
                    if (matchingPallets.length > 0) {
                        searchFiltered[laneId] = {
                            ...laneData,
                            pallets: matchingPallets
                        };
                    }
                }
            });
            filtered = searchFiltered;
        }
        
        // Convert to sorted array for proper lane ordering
        const sortedEntries = Object.entries(filtered).sort(([laneIdA], [laneIdB]) => {
            const parsedA = parseLaneId(laneIdA);
            const parsedB = parseLaneId(laneIdB);
            
            // Sort by warehouse number first (1, 2, 3, ..., 50, 51, ...)
            if (parsedA.warehouse !== parsedB.warehouse) {
                return parsedA.warehouse - parsedB.warehouse;
            }
            
            // Then by lane letter (A, B, C, ...)
            if (parsedA.lane !== parsedB.lane) {
                return parsedA.lane.localeCompare(parsedB.lane);
            }
            
            // Then by location number (1, 2, 3, ..., 999)
            if (parsedA.location !== parsedB.location) {
                return parsedA.location - parsedB.location;
            }
            
            // Finally by position letter (A, B, C, D)
            return parsedA.position.localeCompare(parsedB.position);
        });
        
        // Convert back to object with preserved order
        return Object.fromEntries(sortedEntries);
    }, [localWarehouseData, selectedCustomerId, searchTerm]);

    // Use products from warehouse summary with stock levels and QR codes
    const productsWithStock = useMemo(() => {
        if (!localProducts || !Array.isArray(localProducts)) {
            return [];
        }

        // Map the products from warehouse summary to display format
        return localProducts.map(product => ({
            ...product,
            total_stock: product.quantity_count || 0, // Use quantity_count from warehouse summary
            qr_link: product.product_qr_link || null // Use QR link from warehouse summary
        }));
    }, [localProducts]);

    // Filtered products for the products tab
    const filteredProducts = useMemo(() => {
        let filtered = productsWithStock;
        
        // Filter by customer
        if (selectedCustomerId) {
            filtered = filtered.filter(product => 
                product.customer_id === parseInt(selectedCustomerId)
            );
        }
        
        // Filter by search term
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(product => 
                product.product_code?.toLowerCase().includes(searchLower) ||
                product.product_name?.toLowerCase().includes(searchLower) ||
                product.batch_no?.toLowerCase().includes(searchLower)
            );
        }
        
        return filtered;
    }, [productsWithStock, selectedCustomerId, searchTerm]);

    // Filtered warehouse products for the warehouse tab overview
    const filteredWarehouseProducts = useMemo(() => {
        let filtered = warehouseProducts.length > 0 ? warehouseProducts : localProducts;
        
        // Filter by customer
        if (selectedCustomerId) {
            filtered = filtered.filter(product => 
                product.customer_id === parseInt(selectedCustomerId)
            );
        }
        
        // Filter by search term
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(product => 
                product.product_code?.toLowerCase().includes(searchLower) ||
                product.product_name?.toLowerCase().includes(searchLower) ||
                product.batch_no?.toLowerCase().includes(searchLower)
            );
        }
        
        return filtered;
    }, [warehouseProducts, localProducts, selectedCustomerId, searchTerm]);

    const generateQRCode = async (type, data) => {
        await warehouseFunctions.generateQRCode(type, data, refreshWarehouseData, setIsGeneratingQR);
    };

    // Specialized function for generating QR and opening print label modal
    const generateQRForPrintLabel = async (product) => {
        try {
            // Generate QR code
            await warehouseFunctions.generateQRCode('product', product, refreshWarehouseData, setIsGeneratingQR);
            
            // After generation, find the updated product with QR data and open print modal
            // We'll need to wait a moment for the refresh to complete
            setTimeout(() => {
                const updatedProduct = localProducts.find(p => p.id === product.id);
                if (updatedProduct && updatedProduct.qr_link) {
                    // Pass the actual QR code data instead of the image URL
                    const qrData = updatedProduct.qr_code_data || JSON.stringify({
                        type: "product",
                        product_id: updatedProduct.id,
                        product_code: updatedProduct.product_code,
                        product_name: updatedProduct.product_name,
                        batch_no: updatedProduct.batch_no,
                        customer_id: updatedProduct.customer_id
                    });
                    
                    setLabelModalData({ 
                        qrData: qrData, // Raw QR code data for ZPL
                        qrImageUrl: updatedProduct.qr_link, // Image URL for preview
                        productData: updatedProduct 
                    });
                }
            }, 1000); // Wait 1 second for refresh
        } catch (error) {
            logger.error('Failed to generate QR for print label:', error);
        }
    };

    // Handler for regenerating QR codes
    const handleRegenerateQR = async (itemId, table) => {
        try {
            const result = await regenerateQRCode(itemId, table);
            
            if (result.success) {
                showAlert({
                    message: result.message || 'QR code regenerated successfully!',
                    type: 'success'
                });
                // Refresh the warehouse data to get the updated QR code
                await refreshWarehouseData();
                // Close the QR modal to force re-opening with new data
                setQrModalData(null);
            } else {
                showAlert({
                    message: result.error || result.message || 'Failed to regenerate QR code',
                    type: 'error'
                });
            }
        } catch (error) {
            logger.error('Error regenerating QR code:', error);
            showAlert({
                message: 'Error regenerating QR code',
                type: 'error'
            });
        }
    };

    const createNewLane = () => {
        setPalletEditorOpen(true);
        setEditingPallet(null);
    };

    const createNewProduct = () => {
        setProductEditorOpen(true);
        setEditingProduct(null);
    };

    return (
        <div
            ref={containerRef}
            style={{
                fontFamily: "sans-serif",
                padding: "1rem",
                backgroundColor: color.bg,
                color: color.fg,
                minHeight: "100%",
            }}
        >
            {/* Tab Navigation */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                borderBottom: `2px solid ${color.border}`,
                marginBottom: "1rem",
            }}>
                {/* Main Tabs - Left Side */}
                <div style={{ display: "flex" }}>
                    <button
                        onClick={() => setActiveTab("warehouse")}
                        style={{
                            padding: "0.75rem 1.5rem",
                            backgroundColor: activeTab === "warehouse" ? color.highlight : color.panel,
                            color: activeTab === "warehouse" ? "#000" : color.fg,
                            border: "1px solid grey",
                            borderBottom: activeTab === "warehouse" ? `3px solid ${color.highlight}` : "3px solid transparent",
                            cursor: "pointer",
                            fontSize: "1rem",
                            fontWeight: activeTab === "warehouse" ? "bold" : "normal",
                            borderRadius: '6px 6px 0 0',
                            marginRight: '4px'
                        }}
                    >
                        <FontAwesomeIcon icon={faWarehouse} style={{ width: '20px', height: '20px', marginRight: '12px' }} />Warehouse Lanes
                    </button>
                    <button
                        onClick={() => setActiveTab("products")}
                        style={{
                            padding: "0.75rem 1.5rem",
                            backgroundColor: activeTab === "products" ? color.highlight : color.panel,
                            color: activeTab === "products" ? "#000" : color.fg,
                            border: "1px solid grey",
                            borderBottom: activeTab === "products" ? `3px solid ${color.highlight}` : "3px solid transparent",
                            cursor: "pointer",
                            fontSize: "1rem",
                            fontWeight: activeTab === "products" ? "bold" : "normal",
                            borderRadius: '6px 6px 0 0'
                        }}
                    >
                        <FontAwesomeIcon icon={faClipboardList} style={{ width: '20px', height: '20px', marginRight: '12px' }} />Product Catalogue
                    </button>
                </div>

                {/* Action Tabs - Right Side (only show for warehouse tab) */}
                
                    <div style={{ display: "flex", gap: "4px" }}>
                        {isAdmin && canEdit && (activeTab === "warehouse") && selectedCustomerId && (
                            <button
                                variant="outline-primary"
                                onClick={() =>
                                    setShowHistoricalData(true)
                                }
                                style={{
                                    padding: "0.6rem 1.2rem",
                                    backgroundColor: "rgb(117, 1, 184)",
                                    color: "#fff",
                                    border: "1px solid #a407ff",
                                    borderBottom: "3px solid transparent",
                                    cursor: "pointer",
                                    fontSize: "0.9rem",
                                    fontWeight: "normal",
                                    borderRadius: '6px 6px 0 0'
                                }}
                            >
                                🕘 Historical Data
                            </button>
                        )}

                        {!isWarehouse && activeTab === "warehouse" && (
                        <button
                            onClick={handleDownloadWarehouseLanes}
                            style={{
                                padding: "0.6rem 1.2rem",
                                backgroundColor: "#28a745",
                                color: "#fff",
                                border: "1px solid #28a745",
                                borderBottom: "3px solid transparent",
                                cursor: "pointer",
                                fontSize: "0.9rem",
                                fontWeight: "normal",
                                borderRadius: '6px 6px 0 0'
                            }}
                            title="Download warehouse lanes as Excel"
                        >
                            📊 Download
                        </button>
                        )}
                        {canEdit && activeTab === "warehouse" && (
                            <button
                                onClick={openBulkEditModal}
                                style={{
                                    padding: "0.6rem 1.2rem",
                                    backgroundColor: "#ff4d07ff",
                                    color: "#000",
                                    border: "1px solid #ffc107",
                                    borderBottom: "3px solid transparent",
                                    cursor: "pointer",
                                    fontSize: "0.9rem",
                                    fontWeight: "normal",
                                    borderRadius: '6px 6px 0 0'
                                }}
                                title="Edit multiple empty pallets"
                            >
                                ✏️ Edit Pallets
                            </button>
                        )}
                        {canEdit && activeTab === "warehouse" && (
                            <button
                                onClick={createNewLane}
                                style={{
                                    padding: "0.6rem 1.2rem",
                                    backgroundColor: color.highlight,
                                    color: "#000",
                                    border: `1px solid ${color.highlight}`,
                                    borderBottom: "3px solid transparent",
                                    cursor: "pointer",
                                    fontSize: "0.9rem",
                                    fontWeight: "normal",
                                    borderRadius: '6px 6px 0 0'
                                }}
                            >
                                ➕ New Lane
                            </button>
                        )}
                    </div>


                {/* Action Tabs - Right Side (only show for products tab) */}
                {canEdit && activeTab === "products" && (
                    <div style={{ display: "flex", gap: "4px" }}>
                        <button
                            onClick={handleDownloadProducts}
                            style={{
                                padding: "0.6rem 1.2rem",
                                backgroundColor: "#28a745",
                                color: "#fff",
                                border: "1px solid #28a745",
                                borderBottom: "3px solid transparent",
                                cursor: "pointer",
                                fontSize: "0.9rem",
                                fontWeight: "normal",
                                borderRadius: '6px 6px 0 0'
                            }}
                            title="Download products as Excel"
                        >
                            📊 Download
                        </button>
                    </div>
                )}
            </div>

            {/* Header Controls */}
            <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                marginBottom: "1rem",
                gap: "1rem"
            }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", width: "100%" }}>
                    {/* Customer selector - hidden for customer users and on products tab */}
                    {!isCustomer && activeTab !== "products" && (
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", width: "50%" }}>
                            {customerLocked ? (
                                // Show selected customer name when locked
                                <div style={{
                                    padding: ".5rem .75rem",
                                    minWidth: 220,
                                    flex: 1,
                                    fontSize: "1rem",
                                    backgroundColor: color.headerBg,
                                    color: color.fg,
                                    border: `2px solid ${color.border}`,
                                    borderRadius: 6,
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem"
                                }}>
                                    🔒 {customers.find(c => c.id.toString() === selectedCustomerId)?.company || 'Selected Customer'}
                                </div>
                            ) : (
                                // Show dropdown with Load Data button when not locked
                                <>
                                    <select
                                        value={pendingCustomerId}
                                        onChange={(e) => {
                                            const newCustomerId = e.target.value;
                                            // Clear any existing debounce timeout
                                            if (debounceTimeoutRef.current) {
                                                clearTimeout(debounceTimeoutRef.current);
                                            }
                                            
                                            // Cancel any ongoing request when dropdown changes
                                            if (abortControllerRef.current) {
                                                abortControllerRef.current.abort();
                                            }
                                            
                                            setPendingCustomerId(newCustomerId);
                                            
                                            // Debounce rapid changes (300ms delay)
                                            debounceTimeoutRef.current = setTimeout(() => {
                                                // Auto-load data after a short delay to prevent spamming
                                                // Only load if customer is different from what we have AND what we last processed
                                                if (newCustomerId !== selectedCustomerId && newCustomerId !== lastProcessedCustomerRef.current) {
                                                    loadCustomerData();
                                                }
                                            }, 300);
                                        }}
                                        style={{
                                            padding: ".5rem .75rem",
                                            minWidth: 220,
                                            flex: 1,
                                            fontSize: "1rem",
                                            backgroundColor: color.panel,
                                            color: color.fg,
                                            border: `2px solid ${color.border}`,
                                            borderRadius: 6,
                                            outline: "none",
                                        }}
                                    >
                                        <option value="">All Customers</option>
                                        {customers.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.company}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => {
                                            // Clear any existing debounce timeout since user is manually loading
                                            if (debounceTimeoutRef.current) {
                                                clearTimeout(debounceTimeoutRef.current);
                                            }
                                            
                                            loadCustomerData();
                                        }}
                                        disabled={pendingCustomerId === selectedCustomerId || isLoading}
                                        style={{
                                            padding: ".5rem 1rem",
                                            fontSize: "1rem",
                                            backgroundColor: pendingCustomerId === selectedCustomerId || isLoading ? color.border : color.primaryAccent,
                                            color: pendingCustomerId === selectedCustomerId || isLoading ? color.mutedFg : color.bg,
                                            border: "none",
                                            borderRadius: 6,
                                            cursor: pendingCustomerId === selectedCustomerId || isLoading ? "not-allowed" : "pointer",
                                            fontWeight: "500",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        {isLoading ? "Loading..." : "Load Data"}
                                    </button>
                                </>
                            )}
                            {selectedCustomerId && (
                                <button
                                    onClick={loadAllPallets}
                                    disabled={loadingAllPallets}
                                    style={{
                                        padding: ".5rem 1rem",
                                        fontSize: "1rem",
                                        backgroundColor: loadingAllPallets ? color.border : '#17a2b8',
                                        color: loadingAllPallets ? color.mutedFg : 'white',
                                        border: "none",
                                        borderRadius: 6,
                                        cursor: loadingAllPallets ? "not-allowed" : "pointer",
                                        fontWeight: "500",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {loadingAllPallets ? "Loading..." : "Show All Pallets"}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Show locked customer message */}
                    {!isCustomer && (customerLocked && selectedCustomerId) && (
                        <div style={{
                            padding: ".5rem .75rem",
                            fontSize: "0.9rem",
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            border: '1px solid #ffeaa7',
                            borderRadius: 6,
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem"
                        }}>
                            🔒 Refresh the page to change customer selection.
                        </div>
                    )}

                    {/* Show customer name for customer users */}
                    {isCustomer && profile?.customer_id && (
                        <div style={{
                            padding: ".5rem .75rem",
                            minWidth: 220,
                            width: '50%',
                            fontSize: "1rem",
                            backgroundColor: color.headerBg,
                            color: color.fg,
                            border: `2px solid ${color.border}`,
                            borderRadius: 6,
                            fontWeight: 'bold',
                        }}>
                            {/* Find customer name from allCustomers array */}
                            {customerName || `Customer ID: ${userProfile?.customer_id || 'Unknown'}`}
                        </div>
                    )}

                    <input
                        type="text"
                        placeholder="Search pallets, products (code, name, batch), lanes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            padding: ".5rem .75rem",
                            minWidth: 280,
                            width: '50%',
                            fontSize: "1rem",
                            backgroundColor: color.panel,
                            color: color.fg,
                            border: `2px solid ${color.border}`,
                            borderRadius: 6,
                            outline: "none",
                        }}
                    />
                    
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm("")}
                            style={{
                                padding: "0.5rem",
                                backgroundColor: "transparent",
                                color: color.fg,
                                border: `1px solid ${color.border}`,
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: "0.9rem",
                            }}
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {canEdit && (
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        {activeTab === "warehouse" && (
                            /* Expand/Collapse buttons only */
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                                <button
                                    onClick={expandAllLanes}
                                    style={{
                                        padding: "0.65rem 1rem",
                                        backgroundColor: color.panel,
                                        color: color.fg,
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        fontSize: "0.85rem",
                                        minWidth: "150px"
                                    }}
                                >
                                    📂 Expand All
                                </button>
                                <button
                                    onClick={collapseAllLanes}
                                    style={{
                                        padding: "0.65rem 1rem",
                                        backgroundColor: color.panel,
                                        color: color.fg,
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        fontSize: "0.85rem",
                                        minWidth: "150px"
                                    }}
                                >
                                    📁 Collapse All
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Loading Indicator */}
            {(isLoading || !isDataReady) && (
                <div style={{
                    textAlign: "center",
                    padding: "2rem",
                    backgroundColor: color.panel,
                    border: `2px solid ${color.border}`,
                    borderRadius: 6,
                    marginBottom: "1rem",
                }}>
                    <span style={{ color: color.fg, fontSize: "1.1rem" }}>
                        🔄 {isLoading ? 'Loading warehouse data...' : 'Validating data consistency...'}
                    </span>
                    {!isDataReady && !isLoading && (
                        <div style={{ marginTop: "0.5rem", fontSize: "0.9rem", opacity: 0.7 }}>
                            Data validation in progress. This ensures accurate warehouse information.
                        </div>
                    )}
                </div>
            )}

            {/* Admin User Messages */}
            {!isCustomer && !isLoading && isDataReady && (
                <>
                    {/* No customer selected message */}
                    {!selectedCustomerId && (
                        <div style={{
                            textAlign: "center",
                            padding: "3rem",
                            backgroundColor: color.panel,
                            border: `2px solid ${color.border}`,
                            borderRadius: 8,
                            marginBottom: "1rem",
                        }}>
                            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>👆</div>
                            <p style={{ fontSize: "1.3rem", marginBottom: "0.5rem", fontWeight: "bold" }}>
                                Please select a customer
                            </p>
                            <p style={{ fontSize: "1rem", opacity: 0.7 }}>
                                Choose a customer from the dropdown above to view their warehouse data
                            </p>
                        </div>
                    )}

                    {/* Customer selected but no data message */}
                    {selectedCustomerId && Object.entries(localWarehouseData).length === 0 && warehouseProducts.length === 0 && !isShowingProductDetails && activeTab !== "warehouse" && (
                        <div style={{
                            textAlign: "center",
                            padding: "3rem",
                            backgroundColor: color.panel,
                            border: `2px solid ${color.border}`,
                            borderRadius: 8,
                            marginBottom: "1rem",
                        }}>
                            <div style={{ marginBottom: isShowingProductDetails ? "2rem" : "1rem" }}>
                                <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📭</div>
                                <p style={{ fontSize: "1.3rem", marginBottom: "0.5rem", fontWeight: "bold" }}>
                                    No warehouse data found
                                </p>
                                <p style={{ fontSize: "1rem", opacity: 0.7 }}>
                                    {isShowingProductDetails 
                                        ? "This product has no warehouse activity in the database"
                                        : "The selected customer has no warehouse activity in the database"
                                    }
                                </p>
                            </div>
                            
                            {/* Back button - only show when coming from product selection */}
                            {isShowingProductDetails && (
                                <button
                                    onClick={() => {
                                        setIsShowingProductDetails(false);

                                    }}
                                    style={{
                                        padding: "0.75rem 1.5rem",
                                        backgroundColor: "#007bff",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 8,
                                        cursor: "pointer",
                                        fontSize: "1rem",
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        boxShadow: "0 2px 4px rgba(0,123,255,0.3)",
                                        transition: "all 0.2s ease",
                                        margin: "0 auto"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = "#0056b3";
                                        e.target.style.transform = "translateY(-1px)";
                                        e.target.style.boxShadow = "0 4px 8px rgba(0,123,255,0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = "#007bff";
                                        e.target.style.transform = "translateY(0)";
                                        e.target.style.boxShadow = "0 2px 4px rgba(0,123,255,0.3)";
                                    }}
                                    title="Return to product overview"
                                >
                                    🔙 Back to Product Overview
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Tab Content - Only render when data is ready and conditions are met */}
            {isDataReady && (
                (isCustomer || (!isCustomer && selectedCustomerId))
            ) && activeTab === "warehouse" && (
                <div style={{ marginTop: "1rem" }}>
                    {/* Check if we're showing product-specific details vs customer summary */}
                    {isShowingProductDetails && Object.entries(filteredLanes).length > 0 ? (
                        // Show detailed warehouse structure (original format) when product is selected
                        <>
                            <div style={{
                                padding: "1rem",
                                backgroundColor: color.headerBg,
                                border: `1px solid ${color.border}`,
                                borderRadius: "8px 8px 0 0",
                                marginBottom: "0.5rem"
                            }}>
                                <div style={{ 
                                    display: "flex", 
                                    justifyContent: "space-between", 
                                    alignItems: "center" 
                                }}>
                                    <div>
                                        <h3 style={{ margin: 0, color: color.fg }}>
                                            📦 Warehouse Lanes - Detailed View
                                        </h3>
                                        <p style={{ margin: "0.5rem 0 0 0", opacity: 0.8, fontSize: "0.9rem" }}>
                                            Detailed pallet information for selected product
                                        </p>
                                    </div>
                                    {canBulkMove && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: color.fg }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectableProductPallets.length > 0 && selectedBulkPalletIds.length === selectableProductPallets.length}
                                                    onChange={(event) => setSelectedBulkPalletIds(
                                                        event.target.checked ? selectableProductPallets.map(pallet => pallet.id) : []
                                                    )}
                                                />
                                                Select all standard
                                            </label>
                                            <button
                                                onClick={openBulkMoveModal}
                                                disabled={selectedBulkPalletIds.length === 0}
                                                style={{
                                                    padding: '0.75rem 1rem',
                                                    backgroundColor: selectedBulkPalletIds.length ? color.success : '#777',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: 6,
                                                    cursor: selectedBulkPalletIds.length ? 'pointer' : 'not-allowed',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Move selected ({selectedBulkPalletIds.length})
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => {
                                            setIsShowingProductDetails(false);

                                        }}
                                        style={{
                                            padding: "0.75rem 1.5rem",
                                            backgroundColor: "#007bff",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: 8,
                                            cursor: "pointer",
                                            fontSize: "1rem",
                                            fontWeight: "bold",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            boxShadow: "0 2px 4px rgba(0,123,255,0.3)",
                                            transition: "all 0.2s ease"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = "#0056b3";
                                            e.target.style.transform = "translateY(-1px)";
                                            e.target.style.boxShadow = "0 4px 8px rgba(0,123,255,0.4)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = "#007bff";
                                            e.target.style.transform = "translateY(0)";
                                            e.target.style.boxShadow = "0 2px 4px rgba(0,123,255,0.3)";
                                        }}
                                        title="Return to product overview"
                                    >
                                        🔙 Back to Product Overview
                                    </button>
                                </div>
                            </div>
                            {Object.entries(filteredLanes).map(([laneId, laneData]) => (
                                <LaneDisplay
                                    key={laneId}
                                    laneId={laneId}
                                    laneData={laneData}
                                    color={color}
                                    theme={theme}
                                    isManagement={canEdit}
                                    allCustomers={allCustomers}
                                    canEdit={canEdit}
                                    onEditPallet={(pallet) => {
                                        setEditingPallet(pallet);
                                        setPalletEditorOpen(true);
                                    }}
                                    onGenerateQR={generateQRCode}
                                    onViewQR={(qrData) => setQrModalData(qrData)}
                                    isGeneratingQR={isGeneratingQR}
                                    isExpanded={expandedLanes[laneId] ?? true}
                                    onToggleExpansion={toggleLaneExpansion}
                                    selectedBulkPalletIds={selectedBulkPalletIds}
                                    onToggleBulkPallet={toggleBulkPallet}
                                    canBulkMove={canBulkMove}
                                />
                            ))}
                        </>
                    ) : isShowingProductDetails && Object.entries(filteredLanes).length === 0 ? (
                        // Show "no data for this product" with back button when specific product selected but has no warehouse data
                        <div style={{
                            textAlign: "center",
                            padding: "3rem",
                            backgroundColor: color.panel,
                            border: `2px solid ${color.border}`,
                            borderRadius: 8,
                        }}>
                            <div style={{ marginBottom: "2rem" }}>
                                <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📭</div>
                                <p style={{ fontSize: "1.3rem", marginBottom: "0.5rem", fontWeight: "bold" }}>
                                    No warehouse data found
                                </p>
                                <p style={{ fontSize: "1rem", opacity: 0.7 }}>
                                    This product has no warehouse activity in the database
                                </p>
                            </div>
                            
                            {/* Back button for returning to product overview */}
                            <button
                                onClick={() => {
                                    setIsShowingProductDetails(false);

                                }}
                                style={{
                                    padding: "0.75rem 1.5rem",
                                    backgroundColor: "#007bff",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                    fontWeight: "bold",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    boxShadow: "0 2px 4px rgba(0,123,255,0.3)",
                                    transition: "all 0.2s ease",
                                    margin: "0 auto"
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = "#0056b3";
                                    e.target.style.transform = "translateY(-1px)";
                                    e.target.style.boxShadow = "0 4px 8px rgba(0,123,255,0.4)";
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = "#007bff";
                                    e.target.style.transform = "translateY(0)";
                                    e.target.style.boxShadow = "0 2px 4px rgba(0,123,255,0.3)";
                                }}
                                title="Return to product overview"
                            >
                                🔙 Back to Product Overview
                            </button>
                        </div>
                    ) : isShowingAllPallets ? (
                        // Show All Pallets view
                        <AllPalletsOverview
                            pallets={allPalletsData}
                            onPalletSelect={handlePalletSelect}
                            colorScheme={color}
                            theme={theme}
                            isLoading={loadingAllPallets}
                            loadingPalletDetails={loadingPalletDetails}
                            onBackToProducts={() => {
                                setIsShowingAllPallets(false);
                                setAllPalletsData([]);
                            }}
                        />
                    ) : !isShowingProductDetails ? (
                        // Show ProductOverview component when customer is selected but no specific product
                        <ProductOverview
                            products={filteredWarehouseProducts}
                            onProductSelect={handleProductSelect}
                            customerName={customerName}
                            colorScheme={color}
                            isLoading={isLoading}
                        />
                    ) : (
                        // No data available
                        <div style={{
                            textAlign: "center",
                            padding: "3rem",
                            backgroundColor: color.panel,
                            border: `2px solid ${color.border}`,
                            borderRadius: 8,
                        }}>
                            <p style={{ fontSize: "1.2rem", marginBottom: "1rem" }}>
                                No warehouse lanes found
                            </p>
                            {canEdit && (
                                <button
                                    onClick={createNewLane}
                                    style={{
                                        padding: "0.75rem 1.5rem",
                                        backgroundColor: color.highlight,
                                        color: "#000",
                                        border: "none",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        fontSize: "1rem",
                                    }}
                                >
                                    Create First Lane
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Product Management Tab */}
            {isDataReady && (
                (isCustomer || (!isCustomer && selectedCustomerId))
            ) && activeTab === "products" && (
                <ProductManagementSection
                    products={filteredProducts}
                    color={color}
                    allCustomers={allCustomers}
                    onEditProduct={(product) => {
                        setEditingProduct(product);
                        setProductEditorOpen(true);
                    }}
                    onProductSelect={handleProductSelect}
                    onGenerateQR={generateQRCode}
                    customerName={customerName}
                    onViewQR={(qrData) => setQrModalData(qrData)}
                    onPrintLabel={(qrUrl, product) => setLabelModalData({ qrUrl, productData: product })}
                    onGenerateQRForPrint={generateQRForPrintLabel}
                    isGeneratingQR={isGeneratingQR}
                    onCreateProduct={(canEdit || isCustomer) ? createNewProduct : null}
                    isAdmin={isAdmin}
                    isWarehouse={isWarehouse}
                    canEdit={canEdit}
                    canEditProducts={canEdit || isCustomer}
                />
            )}

            {/* Modals */}
            {palletEditorOpen && (
                <PalletEditor
                    pallet={editingPallet}
                    allCustomers={allCustomers}
                    allPallets={allPallets}
                    color={color}
                    isCustomer={isCustomer}
                    userProfile={userProfile}
                    canEdit={canEdit}
                    onClose={() => {
                        setPalletEditorOpen(false);
                        setEditingPallet(null);
                    }}
                    onSave={async () => {

                        
                        try {
                            // Refresh warehouse data after pallet creation/edit
                            await refreshWarehouseData();
                            
                            // If we're currently showing all pallets, refresh that data too
                            if (isShowingAllPallets && selectedCustomerId) {
                                await loadAllPallets();
                            }
                        } catch (error) {
                            logger.error('❌ Error in onSave callback:', error);
                        }
                        
                        setPalletEditorOpen(false);
                        setEditingPallet(null);

                    }}
                    onRefreshData={async (moveData) => {
                        // For pallet moves, just update the all pallets view if active
                        if (moveData && moveData.type === 'move') {
                            try {
                                const { sourcePalletId, destinationPalletId, movedItem } = moveData;
                                
                                // Update all pallets data (needed for PalletEditor dropdown regardless of current view)
                                if (allCustomerPallets && allCustomerPallets.length > 0) {
                                    const updatedPallets = allCustomerPallets.map(pallet => {
                                        if (pallet.id === sourcePalletId) {
                                            const updatedItems = pallet.items?.filter(item => item.id !== movedItem.id) || [];
                                            const newTotalQuantity = updatedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
                                            return {
                                                ...pallet,
                                                items: updatedItems,
                                                total_quantity: newTotalQuantity,
                                                occupied: updatedItems.length > 0 || newTotalQuantity > 0
                                            };
                                        } else if (pallet.id === destinationPalletId) {
                                            const newItem = { ...movedItem, pallet_id: destinationPalletId };
                                            const updatedItems = [...(pallet.items || []), newItem];
                                            const newTotalQuantity = updatedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
                                            return {
                                                ...pallet,
                                                items: updatedItems,
                                                total_quantity: newTotalQuantity,
                                                occupied: true // Always occupied when we add an item
                                            };
                                        }
                                        return pallet;
                                    });
                                    
                                    setAllCustomerPallets(updatedPallets);
                                } else {
                                    // If allCustomerPallets is empty, build it from current warehouse lanes for the move update
                                    const allPalletsFromLanes = [];
                                    Object.values(warehouseLanes || {}).forEach(lane => {
                                        if (lane.pallet) {
                                            allPalletsFromLanes.push(lane.pallet);
                                        }
                                    });
                                    
                                    if (allPalletsFromLanes.length > 0) {
                                        const updatedPallets = allPalletsFromLanes.map(pallet => {
                                            if (pallet.id === sourcePalletId) {
                                                const updatedItems = pallet.items?.filter(item => item.id !== movedItem.id) || [];
                                                const newTotalQuantity = updatedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
                                                return {
                                                    ...pallet,
                                                    items: updatedItems,
                                                    total_quantity: newTotalQuantity,
                                                    occupied: updatedItems.length > 0 || newTotalQuantity > 0
                                                };
                                            } else if (pallet.id === destinationPalletId) {
                                                const newItem = { ...movedItem, pallet_id: destinationPalletId };
                                                const updatedItems = [...(pallet.items || []), newItem];
                                                const newTotalQuantity = updatedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
                                                return {
                                                    ...pallet,
                                                    items: updatedItems,
                                                    total_quantity: newTotalQuantity,
                                                    occupied: true
                                                };
                                            }
                                            return pallet;
                                        });
                                        
                                        setAllCustomerPallets(updatedPallets);
                                    }
                                }
                                
                                // For lane view, the pallet editor handles its own local state
                                // Parent will be updated on next full refresh when needed
                                
                            } catch (error) {
                                logger.error('❌ Error updating local state after move:', error);
                            }
                        } else {
                            // For non-move operations, do targeted refresh
                            try {
                                if (isShowingAllPallets && selectedCustomerId) {
                                    await loadAllPallets();
                                } else {
                                    await refreshWarehouseData();
                                }
                            } catch (error) {
                                logger.error('❌ Error in targeted refresh:', error);
                            }
                        }
                    }}
                    onUpdatePallet={updatePalletInLocalState}
                />
            )}

            {productEditorOpen && (
                <ProductEditor
                    product={editingProduct}
                    allCustomers={allCustomers}
                    existingProducts={localProducts} // Pass all products for validation
                    isCustomer={isCustomer}
                    userProfile={userProfile}
                    color={color}
                    canEdit={canEdit || isCustomer}
                    onClose={() => {
                        setProductEditorOpen(false);
                        setEditingProduct(null);
                    }}
                    onSave={async () => {

                        // Refresh warehouse data after product creation/edit
                        await refreshWarehouseData();
                        setProductEditorOpen(false);
                        setEditingProduct(null);

                    }}
                />
            )}

            {bulkMoveModalOpen && (
                <BulkMovePalletsModal
                    color={color}
                    selectedCount={selectedBulkPalletIds.length}
                    destinationLanes={destinationLaneOptions}
                    selectedSourceLanes={new Set(selectableProductPallets
                        .filter(pallet => selectedBulkPalletIds.includes(pallet.id))
                        .map(pallet => pallet.lane_id))}
                    isMoving={isBulkMoving}
                    onConfirm={handleBulkMove}
                    onClose={() => !isBulkMoving && setBulkMoveModalOpen(false)}
                />
            )}

            {qrModalData && (
                <QRViewModal
                    qrUrl={qrModalData.qrUrl}
                    title={qrModalData.title}
                    productId={qrModalData.productId}
                    productDescription={qrModalData.productDescription}
                    palletData={qrModalData.palletData}
                    productData={qrModalData.productData}
                    onClose={() => setQrModalData(null)}
                    onRegenerateQR={handleRegenerateQR}
                />
            )}

            {labelModalData && (
                <ProductLabelModal
                    qrUrl={labelModalData.qrUrl}
                    productData={labelModalData.productData}
                    onClose={() => setLabelModalData(null)}
                />
            )}

            {/* Bulk Edit Modal */}
            {/* Bulk Edit Pallets Modal */}
            <BulkEditPalletsModal
                isOpen={bulkEditModalOpen}
                onClose={closeBulkEditModal}
                warehouseLanes={localWarehouseData}
                allPalletsData={allPallets} // Pass the correct allPalletsData array
                allCustomers={customers}
                userProfile={userProfile}
                triggerRefresh={triggerRefresh}
                onRefreshAllPallets={async () => {
                    // Callback to refresh all pallets data when bulk operations complete
                    if (isShowingAllPallets && selectedCustomerId) {

                        await loadAllPallets();
                    }
                }}
                theme={theme}
            />


            {/* Scroll to Top Button */}
            {showScrollToTop && (
                <button
                    onClick={scrollToTop}
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '90px', // Position to the left of AI chat button (56px width + 14px gap + 20px margin)
                        width: '50px',
                        height: '50px',
                        backgroundColor: color.success,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        fontSize: '20px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        zIndex: 999999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = color.success;
                        e.target.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = color.success;
                        e.target.style.transform = 'scale(1)';
                    }}
                    title="Scroll to top"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '-2px' }}>
                        <FontAwesomeIcon icon={faChevronUp} style={{ fontSize: '28px', width: '28px', backgroundColor: color.success }} />
                    </div>
                </button>
            )}

            {/* Historical Warehouse Data Modal */}
            <HistoricalWarehouseData
                theme={theme}
                show={showHistoricalData}
                onHide={() =>
                    setShowHistoricalData(false)
                }
                customerId={selectedCustomer ?? ''}
            />
        </div>
    );
}

function BulkMovePalletsModal({
    color,
    selectedCount,
    destinationLanes,
    selectedSourceLanes,
    isMoving,
    onConfirm,
    onClose
}) {
    const [destinationLane, setDestinationLane] = useState('');
    const [error, setError] = useState('');
    const selectedDestination = destinationLanes.find(lane => lane.laneId === destinationLane);
    const canSubmit = selectedDestination
        && selectedDestination.emptyCount >= selectedCount
        && !selectedSourceLanes.has(destinationLane)
        && !isMoving;

    const submitMove = async () => {
        setError('');
        const result = await onConfirm(destinationLane);
        if (!result.success) {
            setError(result.message || 'Unable to move the selected pallets');
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            backgroundColor: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
        }}>
            <div role="dialog" aria-modal="true" aria-labelledby="bulk-move-title" style={{
                width: 'min(520px, 100%)',
                backgroundColor: color.panel,
                color: color.fg,
                border: `2px solid ${color.border}`,
                borderRadius: 8,
                padding: '1.25rem',
                boxShadow: '0 12px 32px rgba(0,0,0,0.35)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
                    <h3 id="bulk-move-title" style={{ margin: 0 }}>Move {selectedCount} standard pallets</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isMoving}
                        aria-label="Close"
                        style={{ border: 'none', background: 'transparent', color: color.fg, fontSize: '1.5rem', cursor: 'pointer' }}
                    >
                        ×
                    </button>
                </div>

                <p style={{ margin: '1rem 0' }}>
                    Each selected pallet's complete contents will move to a separate empty standard location.
                    The move and adjustment logs are committed together.
                </p>

                <label htmlFor="bulk-destination-lane" style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.4rem' }}>
                    Destination lane
                </label>
                <select
                    id="bulk-destination-lane"
                    value={destinationLane}
                    onChange={(event) => {
                        setDestinationLane(event.target.value);
                        setError('');
                    }}
                    disabled={isMoving}
                    style={{
                        width: '100%',
                        padding: '0.65rem',
                        backgroundColor: color.bg,
                        color: color.fg,
                        border: `1px solid ${color.border}`,
                        borderRadius: 4
                    }}
                >
                    <option value="">Select a lane</option>
                    {destinationLanes.map(lane => {
                        const unavailable = lane.emptyCount < selectedCount || selectedSourceLanes.has(lane.laneId);
                        return (
                            <option key={lane.laneId} value={lane.laneId} disabled={unavailable}>
                                {lane.laneId} - {lane.emptyCount} empty standard location{lane.emptyCount === 1 ? '' : 's'}
                                {unavailable ? ' (unavailable)' : ''}
                            </option>
                        );
                    })}
                </select>

                {destinationLanes.length === 0 && (
                    <p style={{ color: '#dc3545', margin: '0.75rem 0 0' }}>No empty standard pallet locations are available.</p>
                )}
                {error && (
                    <p role="alert" style={{ color: '#dc3545', margin: '0.75rem 0 0', fontWeight: 'bold' }}>{error}</p>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
                    <button type="button" onClick={onClose} disabled={isMoving} style={{ padding: '0.65rem 1rem' }}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submitMove}
                        disabled={!canSubmit}
                        style={{
                            padding: '0.65rem 1rem',
                            border: 'none',
                            borderRadius: 4,
                            backgroundColor: canSubmit ? color.success : '#777',
                            color: '#fff',
                            cursor: canSubmit ? 'pointer' : 'not-allowed',
                            fontWeight: 'bold'
                        }}
                    >
                        {isMoving ? 'Moving...' : `Move ${selectedCount} pallets`}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Lane Display Component
function LaneDisplay({ 
    laneId, 
    laneData, 
    color, 
    theme,
    isManagement, 
    allCustomers, 
    canEdit,
    onEditPallet, 
    onGenerateQR, 
    onViewQR,
    isGeneratingQR,
    isExpanded,
    onToggleExpansion,
    selectedBulkPalletIds,
    onToggleBulkPallet,
    canBulkMove
}) {
    const { pallets } = laneData;
    const isDark = theme === "dark";
    
    // Define theme-appropriate colors for pending order indicators
    const pendingOrderBg = isDark ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 193, 7, 0.1)';
    const pendingOrderBorder = isDark ? '#ffc107' : '#ff8c00';
    const pendingOrderColor = isDark ? '#ffc107' : '#ff8c00';
    
    if (!pallets || pallets.length === 0) {
        return (
            <section
                style={{
                    marginBottom: "2.5rem",
                    border: `3px solid ${color.border}`,
                    borderRadius: 8,
                    backgroundColor: color.panel,
                }}
            >
                <div 
                    onClick={() => onToggleExpansion(laneId, !isExpanded)}
                    style={{
                        margin: "0",
                        padding: "0.75rem 1rem",
                        borderBottom: isExpanded ? `1px solid ${color.border}` : "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                    }}
                >
                    <span style={{ 
                        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", 
                        transition: "transform 0.2s",
                        fontSize: "0.8rem"
                    }}>
                        ▶
                    </span>
                    <h3 style={{ margin: "0" }}>Lane {laneId} - Empty</h3>
                </div>
                {isExpanded && (
                    <div style={{ padding: "1rem", textAlign: "center", opacity: 0.7 }}>
                        No pallets assigned to this lane
                    </div>
                )}
            </section>
        );
    }

    return (
        <section
            style={{
                marginBottom: "2.5rem",
            }}
        >
            {/* Lane Header - Clickable */}
            <div 
                onClick={() => onToggleExpansion(laneId, !isExpanded)}
                style={{
                    border: `3px solid ${color.border}`,
                    borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
                    backgroundColor: color.panel,
                    padding: "0.75rem 1rem",
                    borderBottom: isExpanded ? `2px solid ${color.border}` : `3px solid ${color.border}`,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    transition: "border-radius 0.2s",
                }}
            >
                <span style={{ 
                    transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", 
                    transition: "transform 0.2s",
                    fontSize: "0.8rem"
                }}>
                    ▶
                </span>
                <h3 style={{ margin: "0" }}>
                    Lane {laneId} - {pallets.length} Pallet{pallets.length !== 1 ? 's' : ''}
                </h3>
            </div>
            
            {/* Pallets Container - Collapsible */}
            {isExpanded && (
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    padding: "1rem",
                    border: `3px solid ${color.border}`,
                    borderTop: "none",
                    borderRadius: "0 0 8px 8px",
                    backgroundColor: color.bg,
                }}>
                    {/* Render each pallet as a separate pod */}
                    {pallets.map((pallet, index) => (
                        <PalletDisplay
                            key={pallet.id}
                            pallet={pallet}
                            palletIndex={index}
                            color={color}
                            pendingOrderBg={pendingOrderBg}
                            pendingOrderBorder={pendingOrderBorder}
                            pendingOrderColor={pendingOrderColor}
                            isManagement={canEdit}
                            allCustomers={allCustomers}
                            canEdit={canEdit}
                            onEditPallet={onEditPallet}
                            onGenerateQR={onGenerateQR}
                            onViewQR={onViewQR}
                            isGeneratingQR={isGeneratingQR}
                            isBulkSelected={selectedBulkPalletIds.includes(pallet.id)}
                            onToggleBulkPallet={onToggleBulkPallet}
                            canBulkMove={canBulkMove}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

// New component to display individual pallets as separate pods
function PalletDisplay({
    pallet,
    palletIndex,
    color,
    pendingOrderBg,
    pendingOrderBorder,
    pendingOrderColor,
    isManagement,
    allCustomers,
    canEdit,
    onEditPallet,
    onGenerateQR,
    onViewQR,
    isGeneratingQR,
    isBulkSelected,
    onToggleBulkPallet,
    canBulkMove
}) {
    // Derive customer name from pallet items since pallets no longer carry customer_id
    const customerName = (() => {
        const items = pallet.items || [];
        for (const item of items) {
            const cid = item?.product?.customer_id;
            if (cid) {
                const match = allCustomers.find(c => c.id === cid);
                if (match) return match.company;
            }
        }
        return null;
    })();

    return (
        <div style={{
            border: `2px solid ${pallet.status === 'inactive' ? '#dc3545' : color.border}`,
            borderRadius: "8px",
            backgroundColor: color.panel,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}>
            {/* Pallet Header */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.75rem 1rem",
                borderBottom: `1px solid ${color.border}`,
                backgroundColor: color.headerBg,
                borderRadius: "6px 6px 0 0",
            }}>
                <h4 style={{ margin: "0", color: color.fg }}>
                    🏷️ Pallet {pallet.pallet_code} 
                    {pallet.status === 'inactive' && (
                        <span style={{ 
                            marginLeft: "0.5rem",
                            fontSize: "0.8rem",
                            backgroundColor: "#dc3545",
                            color: "white",
                            padding: "0.2rem 0.5rem",
                            borderRadius: "4px",
                            fontWeight: "bold"
                        }}>
                            INACTIVE
                        </span>
                    )}
                </h4>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    {canBulkMove && pallet.pallet_type === 'standard' && pallet.status === 'active' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={isBulkSelected}
                                onChange={() => onToggleBulkPallet(pallet.id)}
                                onClick={(event) => event.stopPropagation()}
                            />
                            Select
                        </label>
                    )}
                    {/* Pallet QR Code */}
                    <QRButton
                        item={pallet}
                        type="pallet"
                        color={color}
                        onGenerate={() => onGenerateQR('pallet', pallet)}
                        onView={(qrUrl) => onViewQR({
                            qrUrl,
                            title: `Pallet ${pallet.pallet_code} QR Code`,
                            productId: pallet.pallet_code,
                            productDescription: `Pallet in Lane`,
                            palletData: pallet  // Pass complete pallet data
                        })}
                        isGenerating={isGeneratingQR[`pallet-${pallet.id}`]}
                    />
                    
                    {canEdit && (
                        <button
                            onClick={() => onEditPallet(pallet)}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "1.2rem",
                                color: color.fg,
                                padding: "0.25rem",
                            }}
                            title="Edit Pallet"
                        >
                            ✏️
                        </button>
                    )}
                </div>
            </div>

            {/* Pallet Info */}
            <div style={{ padding: "1rem" }}>
                <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
                    gap: "1rem",
                    marginBottom: "1rem"
                }}>
                    <div>
                        <strong>Customer:</strong> {customerName}
                    </div>
                    <div>
                        <strong>Type:</strong> {pallet.pallet_type?.replace('_', ' ') || 'Standard'}
                    </div>
                    <div>
                        <strong>Status:</strong> {pallet.status || 'Active'}
                    </div>
                    <div>
                        <strong>Created:</strong> {new Date(pallet.created_at).toLocaleDateString()}
                    </div>
                </div>

                {/* Items Table */}
                {pallet.items && pallet.items.length > 0 && (
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "1rem" }}>
                        <thead>
                            <tr style={{ backgroundColor: color.headerBg }}>
                                <th style={tableHeaderStyle(color)}>Product Code</th>
                                <th style={tableHeaderStyle(color)}>Product Name</th>
                                <th style={tableHeaderStyle(color)}>Batch Number</th>
                                <th style={tableHeaderStyle(color, "right")}>Quantity</th>
                                <th style={tableHeaderStyle(color, "center")}>QR Code</th>
                                <th style={tableHeaderStyle(color)}>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pallet.items.map((item, itemIndex) => {
                                
                                return (
                                <tr 
                                    key={item.id || `item-${pallet.id}-${itemIndex}`}
                                    style={{
                                        backgroundColor: item.pending_order ? pendingOrderBg : 'transparent',
                                        borderLeft: item.pending_order ? `3px solid ${pendingOrderBorder}` : 'none'
                                    }}
                                    title={item.pending_order ? "⚠️ This item is reserved for a pending order" : ""}
                                >
                                    <td style={tableCellStyle(color)}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            {item.product?.product_code || 'N/A'}
                                            {item.pending_order && (
                                                <span style={{
                                                    fontSize: '0.7rem',
                                                    color: pendingOrderColor,
                                                    fontWeight: 'bold',
                                                    backgroundColor: pendingOrderBg,
                                                    padding: '0.1rem 0.3rem',
                                                    borderRadius: '3px',
                                                    border: `1px solid ${pendingOrderBorder}`
                                                }}>
                                                    RESERVED
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={tableCellStyle(color)}>
                                        {item.product?.product_name || 'N/A'}
                                    </td>
                                    <td style={tableCellStyle(color)}>
                                        {item.product?.batch_no || '-'}
                                    </td>
                                    <td style={tableCellStyle(color, "right")}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                            <span>{item.quantity?.toLocaleString("en-GB") || 0}</span>
                                            {item.pending_order && (
                                                <span style={{
                                                    fontSize: '0.7rem',
                                                    color: '#e17055',
                                                    marginTop: '2px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    Reserved for Order
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={tableCellStyle(color, "center")}>
                                        {item.product && (
                                            <QRButton
                                                item={item.product}
                                                type="product"
                                                color={color}
                                                onGenerate={() => onGenerateQR('product', item.product)}
                                                onView={(qrUrl) => onViewQR({
                                                    qrUrl,
                                                    title: `Product ${item.product.product_code} QR Code`,
                                                    productId: item.product.product_code,
                                                    productDescription: item.product.product_name,
                                                    productData: item.product  // Pass complete product data
                                                })}
                                                isGenerating={isGeneratingQR[`product-${item.product.product_code}`]}
                                            />
                                        )}
                                    </td>
                                    <td style={tableCellStyle(color)}>
                                        {item.notes || '-'}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {(!pallet.items || pallet.items.length === 0) && (
                    <div style={{
                        textAlign: "center",
                        padding: "2rem",
                        opacity: 0.7,
                        fontStyle: "italic",
                        backgroundColor: color.bg,
                        borderRadius: "4px",
                        border: `1px solid ${color.border}`
                    }}>
                        No products on this pallet
                    </div>
                )}
            </div>
        </div>
    );
}

// Product Management Section
function ProductManagementSection({
    products,
    color,
    allCustomers,
    onEditProduct,
    onProductSelect,
    customerName,
    onGenerateQR,
    onViewQR,
    onPrintLabel,
    onGenerateQRForPrint,
    isGeneratingQR,
    onCreateProduct,
    isAdmin,
    isWarehouse,
    canEdit = false,
    canEditProducts = false
}) {
    // Products are already filtered by the parent component


    return (
        <section
            style={{
                marginTop: "1rem",
                border: `3px solid ${color.border}`,
                borderRadius: 8,
                backgroundColor: color.panel,
            }}
        >
            {/* Product Catalog Header with Create Button */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.75rem 1rem",
                borderBottom: `1px solid ${color.border}`,
            }}>
                <h3 style={{ margin: "0" }}>
                    Product Catalog ({products.length} products)
                </h3>
                {onCreateProduct && (
                    <button
                        onClick={onCreateProduct}
                        style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "#28a745",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontSize: "0.9rem",
                        }}
                    >
                        + Add New Product
                    </button>
                )}
            </div>

            {products.length > 0 ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ backgroundColor: color.headerBg }}>
                            <th style={tableHeaderStyle(color)}>Product Code</th>
                            <th style={tableHeaderStyle(color)}>Product Name</th>
                            <th style={tableHeaderStyle(color)}>Customer</th>
                            <th style={tableHeaderStyle(color)}>Description</th>
                            <th style={tableHeaderStyle(color)}>Batch Number</th>
                            <th style={tableHeaderStyle(color, "center")}>Total Stock</th>
                            <th style={tableHeaderStyle(color, "center")}>QR/Print</th>
                            {canEditProducts && (
                                <th style={tableHeaderStyle(color, "center")}>Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((product) => {
                            const customer = allCustomers.find(c => c.id === product.customer_id);
                            return (
                                <tr 
                                    key={product.id}
                                    style={{
                                        cursor: 'default'
                                    }}
                                >
                                    <td style={tableCellStyle(color)}>{product.product_code}</td>
                                    <td style={tableCellStyle(color)}>{product.product_name}</td>
                                    <td style={tableCellStyle(color)}>{customerName || customer?.company || 'Unknown'}</td>
                                    <td style={tableCellStyle(color)}>{product.description || '-'}</td>
                                    <td style={tableCellStyle(color)}>{product.batch_no || '-'}</td>
                                    <td style={tableCellStyle(color, "center")}>
                                        <span style={{ 
                                            fontWeight: "bold",
                                            color: product.total_stock > 0 ? "#28a745" : "#6c757d"
                                        }}>
                                            {product.total_stock || 0}
                                        </span>
                                    </td>
                                    <td style={tableCellStyle(color, "center")}>
                                        <div style={{
                                            display: "flex",
                                            gap: "0.25rem",
                                            justifyContent: "center",
                                            alignItems: "center"
                                        }}>
                                            {product.qr_link ? (
                                                <>
                                                    {/* QR View Button */}
                                                    <button
                                                        onClick={() => {
                                                            onViewQR({
                                                                qrUrl: product.qr_link,
                                                                title: `Product ${product.product_code} QR Code`,
                                                                productId: product.product_code,
                                                                productDescription: product.product_name,
                                                                productData: product
                                                            });
                                                        }}
                                                        disabled={isGeneratingQR[`product-${product.product_code}`]}
                                                        style={{
                                                            background: "white",
                                                            border: "1px solid #ddd",
                                                            borderRadius: "4px",
                                                            padding: "2px",
                                                            cursor: "pointer",
                                                            width: "32px",
                                                            height: "32px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            opacity: isGeneratingQR[`product-${product.product_code}`] ? 0.6 : 1,
                                                        }}
                                                        title="View QR Code"
                                                    >
                                                        <img 
                                                            src={product.qr_link} 
                                                            alt="QR Code"
                                                            style={{ 
                                                                width: "26px",
                                                                height: "26px",
                                                                borderRadius: "2px"
                                                            }}
                                                        />
                                                    </button>
                                                    
                                                    {/* Print Label Button */}
                                                    {canEdit && (
                                                        <button
                                                            onClick={() => onPrintLabel(product.qr_link, product)}
                                                            disabled={isGeneratingQR[`product-${product.product_code}`]}
                                                            style={{
                                                                background: "#e100ffff",
                                                                color: "#000",
                                                                border: "none",
                                                                borderRadius: "4px",
                                                                padding: "0.375rem 0.5rem",
                                                                cursor: "pointer",
                                                                fontSize: "0.75rem",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "0.25rem",
                                                                opacity: isGeneratingQR[`product-${product.product_code}`] ? 0.6 : 1,
                                                            }}
                                                            title="Print Label"
                                                        >
                                                            🖨️
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                /* Single Generate QR Button when no QR exists */
                                                <button
                                                    onClick={() => onGenerateQR('product', product)}
                                                    disabled={isGeneratingQR[`product-${product.product_code}`]}
                                                    style={{
                                                        background: "#ff0000ff",
                                                        color: "#ffffffff",
                                                        border: "none",
                                                        borderRadius: "4px",
                                                        padding: "0.375rem 0.75rem",
                                                        cursor: isGeneratingQR[`product-${product.product_code}`] ? "not-allowed" : "pointer",
                                                        fontSize: "0.75rem",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.25rem",
                                                        opacity: isGeneratingQR[`product-${product.product_code}`] ? 0.6 : 1,
                                                        minWidth: "66px", // Same width as two buttons + gap (32+32+2)
                                                        justifyContent: "center",
                                                    }}
                                                    title="Generate QR Code"
                                                >
                                                    {isGeneratingQR[`product-${product.product_code}`] ? "⏳" : "�"} 
                                                    {isGeneratingQR[`product-${product.product_code}`] ? "Gen..." : "QR"}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    {canEditProducts && (
                                        <td style={tableCellStyle(color, "center")}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEditProduct(product);
                                                }}
                                                style={{
                                                    background: "none",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    fontSize: "1rem",
                                                    color: color.fg,
                                                    padding: "0.25rem",
                                                    borderRadius: "4px",
                                                    transition: "background-color 0.2s ease"
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = color.border;
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = "transparent";
                                                }}
                                                title="Edit Product"
                                            >
                                                ✏️
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            ) : (
                <div style={{
                    padding: "2rem",
                    textAlign: "center",
                    opacity: 0.7
                }}>
                    No products found
                </div>
            )}
        </section>
    );
}

// QR Button Component
function QRButton({ item, type, color, onGenerate, onView, isGenerating }) {
    if (item.qr_link) {
        return (
            <img 
                src={item.qr_link} 
                alt="QR Code"
                style={{ 
                    width: "30px", 
                    height: "30px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "pointer"
                }}
                onClick={() => onView(item.qr_link)}
                title="Click to view full size QR code"
            />
        );
    }

    return (
        <button
            onClick={onGenerate}
            disabled={isGenerating}
            style={{
                padding: "0.25rem 0.5rem",
                fontSize: "0.8rem",
                backgroundColor: isGenerating ? color.warning : color.success,
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: isGenerating ? "not-allowed" : "pointer",
                opacity: isGenerating ? 0.7 : 1,
            }}
            title={`Generate QR code for ${type}`}
        >
            {isGenerating ? "..." : "QR"}
        </button>
    );
}

// Helper functions for table styling
function tableHeaderStyle(color, align = "left") {
    return {
        textAlign: align,
        padding: ".5rem .75rem",
        borderBottom: `1px solid ${color.border}`,
        color: color.fg,
    };
}

function tableCellStyle(color, align = "left") {
    return {
        padding: ".5rem .75rem",
        borderBottom: `1px solid ${color.border}`,
        textAlign: align,
        color: color.fg, // Ensure text color is explicitly set
    };
}

// All Pallets Overview Component
const AllPalletsOverview = ({ 
    pallets = [], 
    onPalletSelect, 
    colorScheme, 
    theme = "light",
    isLoading = false,
    onBackToProducts,
    loadingPalletDetails = false
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    
    const color = colorScheme || {
        bg: "#ffffff",
        fg: "#000000",
        border: "#dee2e6",
        headerBg: "#f8f9fa",
        primary: "#007bff",
        secondary: "#6c757d"
    };

    // Define theme-appropriate colors for pending order indicators
    const isDark = theme === "dark";
    const pendingOrderBg = isDark ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 193, 7, 0.1)';
    const pendingOrderBorder = isDark ? '#ffc107' : '#ff8c00';
    const pendingOrderColor = isDark ? '#ffc107' : '#ff8c00';

    // Filter pallets based on search term
    const filteredPallets = useMemo(() => {
        if (!searchTerm.trim()) {
            return pallets;
        }
        return pallets.filter(pallet => 
            pallet.pallet_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            pallet.lane_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            pallet.lane_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [pallets, searchTerm]);

    const handlePalletClick = (pallet) => {
        if (onPalletSelect) {
            onPalletSelect(pallet);
        }
    };

    if (isLoading) {
        return (
            <div style={{
                backgroundColor: color.bg,
                border: `1px solid ${color.border}`,
                borderRadius: 8,
                padding: "2rem",
                textAlign: "center"
            }}>
                <div style={{ fontSize: "1.2rem", color: color.secondary }}>
                    🔄 Loading all pallets...
                </div>
            </div>
        );
    }

    if (!pallets || pallets.length === 0) {
        return (
            <div style={{
                backgroundColor: color.bg,
                border: `1px solid ${color.border}`,
                borderRadius: 8,
                padding: "2rem",
                textAlign: "center"
            }}>
                <div style={{ fontSize: "1.2rem", color: color.secondary, marginBottom: "1rem" }}>
                    📍 No pallets found for this customer
                </div>
                <button
                    onClick={onBackToProducts}
                    style={{
                        padding: "0.75rem 1.5rem",
                        backgroundColor: "#6c757d",
                        color: "white",
                        border: "none",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontSize: "1rem",
                        fontWeight: "500",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#5a6268";
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#6c757d";
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                    }}
                >
                    🔙 Back to Products
                </button>
            </div>
        );
    }

    return (
        <div style={{
            backgroundColor: color.bg,
            border: `1px solid ${color.border}`,
            borderRadius: 8,
            overflow: "hidden"
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: color.headerBg,
                padding: "1rem",
                borderBottom: `1px solid ${color.border}`
            }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1rem"
                }}>
                    <h3 style={{ margin: 0, color: color.fg }}>
                        📍 All Pallets ({filteredPallets.length}{pallets.length !== filteredPallets.length ? ` of ${pallets.length}` : ''})
                    </h3>
                    <button
                    onClick={onBackToProducts}
                    style={{
                        padding: "0.5rem 1rem",
                        backgroundColor: "#6c757d",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: "500",
                        transition: "all 0.2s ease",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#5a6268";
                        e.target.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#6c757d";
                        e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                    }}
                >
                    🔙 Back to Products
                </button>
                </div>
                
                {/* Search Input */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem"
                }}>
                    <span style={{ 
                        fontSize: "0.9rem", 
                        color: color.fg,
                        fontWeight: "500",
                        minWidth: "60px"
                    }}>
                        Filter:
                    </span>
                    <input
                        type="text"
                        placeholder="Search by pallet code or lane..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            flex: 1,
                            padding: "0.5rem 0.75rem",
                            fontSize: "0.9rem",
                            backgroundColor: color.bg,
                            color: color.fg,
                            border: `2px solid ${color.border}`,
                            borderRadius: 6,
                            outline: "none",
                            transition: "border-color 0.2s ease"
                        }}
                        onFocus={(e) => e.target.style.borderColor = color.primary}
                        onBlur={(e) => e.target.style.borderColor = color.border}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm("")}
                            style={{
                                padding: "0.5rem",
                                backgroundColor: "transparent",
                                color: color.secondary,
                                border: "none",
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: "1rem",
                                lineHeight: 1
                            }}
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Pallets Table */}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ backgroundColor: color.headerBg }}>
                            <th style={tableHeaderStyle(color)}>Status</th>
                            <th style={tableHeaderStyle(color)}>Pallet Code</th>
                            <th style={tableHeaderStyle(color)}>Lane</th>
                            <th style={tableHeaderStyle(color)}>Type</th>
                            <th style={tableHeaderStyle(color)}>Occupancy</th>
                            <th style={tableHeaderStyle(color)}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPallets.map((pallet, index) => {
                            const isOccupied = pallet.occupied || (pallet.items && pallet.items.length > 0) || pallet.total_quantity > 0;
                            const isInactive = pallet.pallet_status !== 'active';
                            const hasReservedItems = pallet.items && pallet.items.some(item => item.pending_order);
                            
                            // DEBUG: Log pallet with reserved items
                            if (hasReservedItems) {
                                logger.info(`🎯 PALLET ${pallet.pallet_code} HAS RESERVED ITEMS:`, {
                                    pallet_id: pallet.id,
                                    items_count: pallet.items?.length,
                                    reserved_items: pallet.items?.filter(item => item.pending_order)
                                });
                            }
                            
                            const baseRowBg = index % 2 === 0 ? color.bg : color.headerBg;
                            const rowBg = hasReservedItems ? pendingOrderBg : baseRowBg;
                            const rowBorder = isInactive ? '2px solid #dc3545' : (hasReservedItems ? `1px solid ${pendingOrderBorder}` : 'none');
                            
                            return (
                                <tr 
                                    key={pallet.id || index}
                                    style={{
                                        backgroundColor: rowBg,
                                        border: rowBorder,
                                        cursor: "pointer"
                                    }}
                                    title={hasReservedItems ? "⚠️ This pallet contains items reserved for pending orders" : ""}
                                    onClick={() => handlePalletClick(pallet)}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = color.primary + '20'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rowBg}
                                >
                                    <td style={tableCellStyle(color, "center")}>
                                        <div style={{
                                            padding: '0.25rem 0.5rem',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            backgroundColor: isOccupied ? '#dc3545' : '#28a745',
                                            color: 'white',
                                            display: 'inline-block'
                                        }}>
                                            {isOccupied ? 'OCCUPIED' : 'AVAILABLE'}
                                        </div>
                                    </td>
                                    <td style={tableCellStyle(color)}>
                                        <strong>{pallet.pallet_code}</strong>
                                        {isInactive && (
                                            <div style={{ 
                                                fontSize: '0.7rem', 
                                                color: '#dc3545', 
                                                fontWeight: 'bold' 
                                            }}>
                                                INACTIVE
                                            </div>
                                        )}
                                        {hasReservedItems && (
                                            <div style={{ 
                                                fontSize: '0.7rem', 
                                                color: pendingOrderColor, 
                                                fontWeight: 'bold' 
                                            }}>
                                                HAS RESERVED ITEMS
                                            </div>
                                        )}
                                    </td>
                                    <td style={tableCellStyle(color)}>{pallet.lane_name || pallet.lane_id}</td>
                                    <td style={tableCellStyle(color)}>
                                        {pallet.pallet_type && (
                                            <span style={{
                                                fontSize: '0.7rem',
                                                backgroundColor: '#6c757d',
                                                color: 'white',
                                                padding: '0.15rem 0.4rem',
                                                borderRadius: '3px'
                                            }}>
                                                {pallet.pallet_type.toUpperCase()}
                                            </span>
                                        )}
                                    </td>
                                    <td style={tableCellStyle(color)}>
                                        {isOccupied ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <span style={{ color: '#ffc107', fontWeight: 'bold' }}>
                                                    {pallet.items?.length ? `${pallet.items.length} items` : `Qty: ${pallet.total_quantity || 0}`}
                                                </span>
                                                {pallet.items && pallet.items.some(item => item.pending_order) && (
                                                    <span style={{
                                                        fontSize: '0.7rem',
                                                        color: '#e17055',
                                                        fontWeight: 'bold',
                                                        marginTop: '2px'
                                                    }}>
                                                        ⚠️ Has Reserved Items
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span style={{ color: color.secondary }}>Empty</span>
                                        )}
                                    </td>
                                    <td style={{...tableCellStyle(color, "center"), textAlign: "center"}}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handlePalletClick(pallet);
                                            }}
                                            disabled={loadingPalletDetails}
                                            style={{
                                                padding: "0.4rem 0.8rem",
                                                fontSize: "0.8rem",
                                                backgroundColor: loadingPalletDetails ? "#6c757d" : (isOccupied ? "#17a2b8" : '#28a745'),
                                                color: "white",
                                                border: "none",
                                                borderRadius: 6,
                                                cursor: loadingPalletDetails ? "not-allowed" : "pointer",
                                                fontWeight: "500",
                                                transition: "all 0.2s ease",
                                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                minWidth: "90px",
                                                opacity: loadingPalletDetails ? 0.7 : 1,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "0.25rem"
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!loadingPalletDetails) {
                                                    e.target.style.backgroundColor = isOccupied ? "#138496" : '#218838';
                                                    e.target.style.transform = "translateY(-1px)";
                                                    e.target.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!loadingPalletDetails) {
                                                    e.target.style.backgroundColor = isOccupied ? "#17a2b8" : '#28a745';
                                                    e.target.style.transform = "translateY(0)";
                                                    e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                                }
                                            }}
                                        >
                                            {loadingPalletDetails ? (
                                                <>
                                                    <span style={{ 
                                                        display: "inline-block",
                                                        width: "12px",
                                                        height: "12px",
                                                        border: "2px solid transparent",
                                                        borderTop: "2px solid white",
                                                        borderRadius: "50%",
                                                        animation: "spin 1s linear infinite"
                                                    }}></span>
                                                    Loading...
                                                </>
                                            ) : (
                                                isOccupied ? 'View' : 'Add Products'
                                            )}
                                        </button>
                                        
                                        {/* Add CSS animation for spinner */}
                                        <style>{`
                                            @keyframes spin {
                                                0% { transform: rotate(0deg); }
                                                100% { transform: rotate(360deg); }
                                            }
                                        `}</style>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                
                {/* No results message */}
                {filteredPallets.length === 0 && pallets.length > 0 && (
                    <div style={{
                        textAlign: "center",
                        padding: "2rem",
                        color: color.secondary,
                        backgroundColor: color.bg
                    }}>
                        <div style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
                            🔍 No pallets match "{searchTerm}"
                        </div>
                        <div style={{ fontSize: "0.9rem" }}>
                            Try searching by pallet code or lane name
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

EnhancedWarehouseManagement.propTypes = {
    theme: PropTypes.string.isRequired,
    management: PropTypes.bool,
    customers: PropTypes.arrayOf(PropTypes.object).isRequired,
    allCustomers: PropTypes.arrayOf(PropTypes.object),
    warehouseLanes: PropTypes.object,
    warehouseProducts: PropTypes.arrayOf(PropTypes.object),
    triggerRefresh: PropTypes.func,
    isCustomer: PropTypes.bool,
    profile: PropTypes.object,
};